import React, { useState, useEffect } from 'react';
import Calling from '../../api/Calling';
import { toast } from 'react-toastify';
import Navbar from './Navbar';
import {
    FaSearch, FaTrash, FaQuestionCircle, FaCheckCircle, FaFilter,
    FaChevronLeft, FaChevronRight, FaLightbulb, FaFire, FaCheck,
    FaTimes, FaChartBar, FaListOl
} from 'react-icons/fa';
import { MdQuiz, MdAnalytics } from 'react-icons/md';
import generateCaptcha from '../../components/captcha';


const QuestionShow = () => {
    const [loader, setLoader] = useState(false);
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedQuestions, setSelectedQuestions] = useState([]);
    const [data, setData] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [selectAll, setSelectAll] = useState(false);
    const [totalQuestions, setTotalQuestions] = useState(0);
    const [questionType, setQuestionType] = useState('all');
    const [showLimit, setShowLimit] = useState(200); // Default show limit

    const fetchData = async () => {
        setLoader(true);
        try {
            const response = await Calling.getQuestion(currentPage, showLimit, questionType, search);
            if (response?.status) {
                setData(response?.data);
                setTotalPages(response?.pagination?.totalPages);
                setTotalQuestions(response?.pagination?.totalRecords);
            } else {
                toast.error(response?.message);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line
    }, [currentPage, questionType, showLimit]);

    const handleSelectAll = () => {
        if (!selectAll) {
            const allQuestionIds = data.map((question) => question._id);
            setSelectedQuestions(allQuestionIds);
        } else {
            setSelectedQuestions([]);
        }
        setSelectAll(!selectAll);
    };

    const handleSelectQuestion = (id) => {
        if (selectedQuestions.includes(id)) {
            setSelectedQuestions(selectedQuestions.filter((questionId) => questionId !== id));
        } else {
            setSelectedQuestions([...selectedQuestions, id]);
        }
    };

    const handleSearch = (e) => {
        setSearch(e.target.value);
        setCurrentPage(1);
        if (e.target.value.length === 0 || e.target.value.length > 2) {
            fetchData();
        }
    };

    // Reset to page 1 when changing the show limit
    const handleShowLimitChange = (e) => {
        setShowLimit(Number(e.target.value));
        setCurrentPage(1);
    };

    const handleDelete = async () => {
        if (selectedQuestions.length === 0) {
            toast.error('Please select questions to delete');
            return;
        }
        try {
            const content = `You are about to delete questions. To confirm, please solve this math problem.`;
            const check = generateCaptcha(content);
            if (!check) {
                return toast.error('Captcha verification failed.');
            }
            const response = await Calling.DeleteQuestions({ id: selectedQuestions });
            if (response?.status) {
                toast.success(response?.message);
                setSelectedQuestions([]);
                setSelectAll(false);
                fetchData();
            } else {
                toast.error(response?.message);
            }
        } catch (error) {
            console.error('Delete error:', error);
            toast.error('Failed to delete questions');
        }
    };

    const getDifficultyIcon = (type) => {
        switch (type) {
            case 'easy': return <FaLightbulb className="w-3 h-3" />;
            case 'medium': return <FaChartBar className="w-3 h-3" />;
            case 'hard': return <FaFire className="w-3 h-3" />;
            default: return <FaQuestionCircle className="w-3 h-3" />;
        }
    };

    const getDifficultyColor = (type) => {
        switch (type) {
            case 'easy': return 'bg-green-50 text-green-800 border-green-200';
            case 'medium': return 'bg-main-yellow/10 text-main-font border-main-yellow/30';
            case 'hard': return 'bg-main-delete/10 text-main-delete border-main-delete/30';
            default: return 'bg-gray-100 text-main-subfont border-main-border';
        }
    };

    return (
        <div className="w-full text-main-font mt-16 px-3 md:ml-[4.4rem]">
            <h1 className="text-xl md:text-2xl font-bold mt-4 text-main-font w-auto">Question Management</h1>
            <div className='bg-main-white rounded-xl shadow-lg'>
                <Navbar selectState={'questionshow'} />
                <hr className='border-main-border' />

                <div className="p-6">
                    {/* Compact Header */}
                    <div className="bg-gradient-to-r from-main-th to-main-bg rounded-lg p-3 mb-4 border border-main-border">
                        <div className="flex items-center gap-2">
                            <div className="p-2 bg-gradient-to-r from-main-blue to-primary-purple rounded-lg">
                                <MdQuiz className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-main-font">Question Database</h2>
                                <p className="text-sm text-main-subfont">Manage your quiz questions</p>
                            </div>
                        </div>
                    </div>

                    {/* Compact Controls Row - First Row */}
                    <div className="flex flex-wrap gap-2 mb-2">
                        {/* Stats */}
                        <div className="flex items-center bg-main-white rounded-lg shadow-sm border border-main-border p-2 gap-2">
                            <MdAnalytics className="w-4 h-4 text-main-blue" />
                            <span className="text-sm text-main-subfont">Total:</span>
                            <span className="text-md font-bold text-main-font">{totalQuestions.toLocaleString()}</span>
                        </div>

                        {/* Search */}
                        <div className="flex-1 min-w-[200px]">
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Search questions..."
                                    value={search}
                                    onChange={handleSearch}
                                    className="w-full pl-8 pr-3 py-2 text-sm border border-main-border rounded-lg focus:outline-none focus:ring-1 focus:ring-main-blue focus:border-main-blue bg-main-bg"
                                />
                                <FaSearch className="absolute left-2 top-1/2 transform -translate-y-1/2 text-main-subfont w-4 h-4" />
                            </div>
                        </div>

                        {/* Delete Button - Always visible but disabled if none selected */}
                        <button
                            onClick={handleDelete}
                            disabled={selectedQuestions.length === 0}
                            className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${selectedQuestions.length > 0
                                ? "bg-main-delete text-white hover:bg-main-delete/90"
                                : "bg-gray-200 text-gray-500 cursor-not-allowed"
                                }`}
                        >
                            <FaTrash className="w-3 h-3" />
                            {selectedQuestions.length > 0 ? `Delete (${selectedQuestions.length})` : 'Delete'}
                        </button>
                    </div>

                    {/* Second Row for Filters */}
                    <div className="flex flex-wrap gap-2 mb-4">
                        {/* Difficulty Filter */}
                        <div className="flex items-center gap-1 min-w-[150px]">
                            <FaFilter className="w-3 h-3 text-main-blue" />
                            <select
                                value={questionType}
                                onChange={(e) => setQuestionType(e.target.value)}
                                className="text-sm p-2 border border-main-border rounded-lg focus:outline-none focus:ring-1 focus:ring-main-blue bg-main-bg"
                            >
                                <option value="all">All Difficulties</option>
                                <option value="easy">Easy</option>
                                <option value="medium">Medium</option>
                                <option value="hard">Hard</option>
                            </select>
                        </div>

                        {/* Show Limit Dropdown */}
                        <div className="flex items-center gap-1 min-w-[150px]">
                            <FaListOl className="w-3 h-3 text-main-blue" />
                            <select
                                value={showLimit}
                                onChange={handleShowLimitChange}
                                className="text-sm p-2 border border-main-border rounded-lg focus:outline-none focus:ring-1 focus:ring-main-blue bg-main-bg"
                            >
                                <option value={200}>200 Per Page</option>
                                <option value={500}>500 Per Page</option>
                                <option value={1000}>1000 Per Page</option>
                            </select>
                        </div>
                    </div>

                    {/* Questions Table */}
                    {loader ? (
                        <div className="rounded-lg shadow-sm border border-main-border p-8 text-center">
                            <div className="animate-spin w-6 h-6 border-2 border-main-blue/20 border-t-main-blue rounded-full mx-auto mb-2"></div>
                            <p className="text-main-subfont text-sm">Loading...</p>
                        </div>
                    ) : data?.length > 0 ? (
                        <div className="bg-main-white rounded-lg shadow-sm border border-main-border overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-gradient-to-r from-main-blue to-primary-purple text-white">
                                        <tr>
                                            <th className="p-2 text-left w-10">#</th>
                                            <th className="p-2 text-center w-10">
                                                <input
                                                    type="checkbox"
                                                    checked={selectAll}
                                                    onChange={handleSelectAll}
                                                    className="w-4 h-4 rounded border-white/20"
                                                />
                                            </th>
                                            <th className="p-2 text-left">Question</th>
                                            <th className="p-2 text-center w-24">Level</th>
                                            <th className="p-2 text-left">Options</th>
                                            <th className="p-2 text-center w-16">Answer</th>
                                            <th className="p-2 text-center w-16">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-main-border">
                                        {data.map((question, index) => (
                                            <tr
                                                key={question._id}
                                                className={`transition-colors hover:bg-main-th ${selectedQuestions.includes(question._id) ? 'bg-main-th' : ''}`}
                                            >
                                                <td className="p-2 text-main-subfont">{(currentPage - 1) * showLimit + index + 1}</td>
                                                <td className="p-2 text-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedQuestions.includes(question._id)}
                                                        onChange={() => handleSelectQuestion(question._id)}
                                                        className="w-4 h-4 rounded border-main-border"
                                                    />
                                                </td>
                                                <td className="p-2">
                                                    <div className="flex items-start gap-1">
                                                        <FaQuestionCircle className="w-3 h-3 text-main-blue mt-1 flex-shrink-0" />
                                                        <p className="text-main-font">{question.Q}</p>
                                                    </div>
                                                </td>
                                                <td className="p-2 text-center">
                                                    <div className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${getDifficultyColor(question.type)}`}>
                                                        {getDifficultyIcon(question.type)}
                                                        <span className="capitalize">{question.type}</span>
                                                    </div>
                                                </td>
                                                <td className="p-2">
                                                    <div className="grid grid-cols-2 gap-1">
                                                        {['A', 'B', 'C', 'D'].map((option) => (
                                                            <div key={option} className={`flex items-center gap-1 p-1 rounded text-xs ${question.Ans === option
                                                                ? 'bg-green-50 text-green-800'
                                                                : 'bg-main-bg'
                                                                }`}>
                                                                <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-xs ${question.Ans === option
                                                                    ? 'bg-green-500 text-white'
                                                                    : 'bg-gray-200 text-gray-600'
                                                                    }`}>{option}</span>
                                                                <span className="truncate" title={question[option]}>
                                                                    {question[option]?.length > 15 ?
                                                                        `${question[option]?.substring(0, 15)}...` :
                                                                        question[option]}
                                                                </span>
                                                                {question.Ans === option && (
                                                                    <FaCheck className="w-3 h-3 text-green-600 ml-auto" />
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </td>
                                                <td className="p-2 text-center">
                                                    <div className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-500 text-white font-bold text-xs">
                                                        {question.Ans}
                                                    </div>
                                                </td>
                                                <td className="p-2 text-center">
                                                    <button
                                                        onClick={() => handleSelectQuestion(question._id)}
                                                        className="p-1 text-main-subfont hover:text-main-delete"
                                                        title="Select for deletion"
                                                    >
                                                        <FaTrash className="w-3 h-3" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Compact Pagination */}
                            <div className="bg-main-th px-3 py-2 border-t border-main-border">
                                <div className="flex justify-between items-center text-sm">
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                            disabled={currentPage === 1}
                                            className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${currentPage === 1
                                                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                                                : "bg-main-blue text-white hover:bg-main-blue/90"
                                                }`}
                                        >
                                            <FaChevronLeft className="w-2 h-2" />
                                            Prev
                                        </button>

                                        <button
                                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                            disabled={currentPage === totalPages}
                                            className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${currentPage === totalPages
                                                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                                                : "bg-main-blue text-white hover:bg-main-blue/90"
                                                }`}
                                        >
                                            Next
                                            <FaChevronRight className="w-2 h-2" />
                                        </button>
                                    </div>

                                    <div className="text-xs text-main-subfont">
                                        <span>Page {currentPage} of {totalPages} | </span>
                                        <span>
                                            {((currentPage - 1) * showLimit + 1).toLocaleString()} - {Math.min(currentPage * showLimit, totalQuestions).toLocaleString()} of {totalQuestions.toLocaleString()}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-main-white rounded-lg shadow-sm border border-main-border p-8 text-center">
                            <FaQuestionCircle className="w-8 h-8 mx-auto mb-2 text-main-subfont" />
                            <h3 className="text-lg font-bold text-main-font mb-1">No Questions Found</h3>
                            <p className="text-sm text-main-subfont">Try adjusting your search criteria.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default QuestionShow;